import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { AtelierMobileMenu } from "./AtelierMobileMenu";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";

function AtelierStyles() {
    return (
        <style>{`
            [data-web-template="atelier-muse"] .site-builder-section {
                padding-inline: clamp(1rem, 3vw, 2.5rem);
                position: relative;
            }

            [data-web-template="atelier-muse"] .site-builder-grid {
                display: grid;
                gap: clamp(1rem, 2vw, 1.75rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="atelier-muse"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="atelier-muse"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="atelier-muse"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.1rem;
                min-width: 0;
            }

            [data-web-template="atelier-muse"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="atelier-muse"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(1rem, 2.4vw, 2.25rem);
                justify-content: center;
            }

            [data-web-template="atelier-muse"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template="atelier-muse"] .site-builder-navigation-link,
            [data-web-template="atelier-muse"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                font-size: .76rem;
                font-weight: 800;
                letter-spacing: .08em;
                position: relative;
                text-transform: uppercase;
                width: auto !important;
            }

            [data-web-template="atelier-muse"] .site-builder-navigation-link::before,
            [data-web-template="atelier-muse"] [data-document-role="headerNavigation"] .site-builder-text::before {
                border-top: 1px solid currentColor;
                content: "";
                left: 0;
                position: absolute;
                right: 100%;
                top: calc(50% - 1px);
                transition: right 180ms ease;
            }

            [data-web-template="atelier-muse"] .site-builder-navigation-link:hover::before,
            [data-web-template="atelier-muse"] [data-document-role="headerNavigation"] .site-builder-text:hover::before {
                right: 0;
            }

            [data-web-template="atelier-muse"] [data-web-region="header"] {
                position: sticky;
                top: 0;
                z-index: 40;
            }

            [data-web-template="atelier-muse"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="atelier-muse"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="atelier-muse"] [data-document-role="headerCta"],
            [data-web-template="atelier-muse"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="atelier-muse"] [data-document-role="heroVisual"],
            [data-web-template="atelier-muse"] [data-document-role="editorialVisual"],
            [data-web-template="atelier-muse"] [data-document-role="transformationPortrait"],
            [data-web-template="atelier-muse"] [data-document-role="transformationDetail"],
            [data-web-template="atelier-muse"] [data-document-role="lookbookImage"] {
                position: relative;
            }

            [data-web-template="atelier-muse"] [data-document-role="heroVisual"] .site-builder-image,
            [data-web-template="atelier-muse"] [data-document-role="editorialVisual"] .site-builder-image,
            [data-web-template="atelier-muse"] [data-document-role="transformationPortrait"] .site-builder-image,
            [data-web-template="atelier-muse"] [data-document-role="transformationDetail"] .site-builder-image,
            [data-web-template="atelier-muse"] [data-document-role="lookbookImage"] .site-builder-image {
                min-height: 100%;
            }

            [data-web-template="atelier-muse"] [data-document-role="heroFloatingNote"] {
                bottom: clamp(1rem, 4vw, 2.5rem);
                left: clamp(-2rem, -2vw, -1rem);
                position: absolute;
                transform: rotate(-3deg);
                z-index: 2;
            }

            [data-web-template="atelier-muse"] [data-web-region="signatureStrip"] > .site-builder-grid > .site-builder-grid-item:not(:last-child) {
                border-right: 1px solid var(--site-border);
                padding-right: clamp(1rem, 2vw, 2rem);
            }

            [data-web-template="atelier-muse"] [data-web-region="lookbookGallery"] > .site-builder-grid {
                gap: 0;
            }

            [data-web-template="atelier-muse"] [data-web-region="footer"] .site-builder-navigation {
                align-items: flex-start;
                flex-direction: column;
                gap: .65rem;
            }

            [data-web-template="atelier-muse"] [data-web-region="footer"] :where(
                [data-document-role="footerNavigation"],
                [data-document-role="footerContact"]
            ) .site-builder-text,
            [data-web-template="atelier-muse"] [data-web-region="footer"] :where(
                [data-document-role="footerNavigation"],
                [data-document-role="footerContact"]
            ) .site-builder-text > span {
                color: var(--atelier-ivory) !important;
            }

            [data-web-template="atelier-muse"] {
                --atelier-coral: #ed7265;
                --atelier-coral-on-dark: #ff9c91;
                --atelier-ivory: #fffaf0;
                --atelier-plum-glass: rgba(39, 13, 35, .92);
                scroll-behavior: smooth;
                scroll-padding-top: 76px;
            }

            [data-web-template="atelier-muse"] [data-web-region="header"] {
                background: #1f0c1c !important;
                border-color: transparent !important;
                color: var(--atelier-ivory);
                left: 0;
                min-height: 104px;
                padding-inline: clamp(1rem, 3vw, 2.5rem);
                position: fixed !important;
                right: 0;
                transition: background 320ms ease, box-shadow 320ms ease, min-height 320ms ease;
            }

            [data-web-template="atelier-muse"] [data-web-region="header"] [data-document-role="brand"] .site-builder-text,
            [data-web-template="atelier-muse"] [data-web-region="header"] [data-document-role="brand"] .site-builder-text > span,
            [data-web-template="atelier-muse"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text,
            [data-web-template="atelier-muse"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text > span,
            [data-web-template="atelier-muse"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text,
            [data-web-template="atelier-muse"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text > span,
            [data-web-template="atelier-muse"] [data-web-region="header"] .site-navigation-primary-link,
            [data-web-template="atelier-muse"] [data-web-region="header"] .site-navigation-primary-trigger {
                color: var(--atelier-ivory) !important;
            }

            [data-web-template="atelier-muse"]:has([data-web-region="hero"]) [data-web-region="header"]:not([data-atelier-header-scrolled]) .site-navigation-marker-cinematic {
                color: var(--atelier-coral-on-dark) !important;
            }

            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] {
                backdrop-filter: blur(18px) saturate(125%);
                background: rgba(244, 236, 223, .94) !important;
                border-color: rgba(27, 23, 28, .18) !important;
                box-shadow: 0 16px 46px rgba(27, 23, 28, .11);
                color: var(--site-text);
                min-height: 68px;
            }

            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] [data-document-role="brand"] .site-builder-text,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] [data-document-role="brand"] .site-builder-text > span,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text > span,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] [data-document-role="headerNavigation"] .site-builder-text,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] [data-document-role="headerNavigation"] .site-builder-text > span,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] .site-navigation-primary-link,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] .site-navigation-primary-trigger {
                color: var(--site-text) !important;
            }

            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) {
                backdrop-filter: blur(18px) saturate(125%);
                background: rgba(244, 236, 223, .96) !important;
                border-color: rgba(27, 23, 28, .18) !important;
                box-shadow: 0 10px 34px rgba(27, 23, 28, .08);
                color: var(--site-text);
            }

            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) [data-document-role="brand"] .site-builder-text,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) [data-document-role="brand"] .site-builder-text > span,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text > span,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) [data-document-role="headerNavigation"] .site-builder-text,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) [data-document-role="headerNavigation"] .site-builder-text > span,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) .site-navigation-primary-link,
            [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"]:not([data-atelier-mobile-menu-open]) .site-navigation-primary-trigger {
                color: var(--site-text) !important;
            }

            [data-web-template="atelier-muse"] .site-builder-form :where(input, textarea)::placeholder {
                color: var(--site-muted);
                opacity: 1;
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"] {
                background: var(--site-primary) !important;
                min-height: 100svh;
                overflow: hidden;
                padding: 0;
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"]::after {
                background:
                    linear-gradient(90deg, rgba(18, 7, 16, .88) 0%, rgba(31, 12, 28, .6) 43%, rgba(31, 12, 28, .08) 78%),
                    linear-gradient(0deg, rgba(10, 4, 9, .48), transparent 52%);
                content: "";
                inset: 0;
                pointer-events: none;
                position: absolute;
                z-index: 1;
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"] > .site-builder-grid {
                align-content: stretch;
                min-height: 100svh;
                position: relative;
                width: 100%;
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroVisual"] {
                grid-column: 1 / -1 !important;
                grid-row: 1 / -1 !important;
                inset: 0;
                min-height: 100svh;
                position: absolute;
                width: 100%;
                z-index: 0;
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroVisual"] .site-builder-image {
                height: 100svh !important;
                min-height: 100svh !important;
                object-position: center 24% !important;
                transform: scale(1.035);
                transition: transform 7.6s cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="atelier-muse"][data-atelier-runtime-ready] [data-web-region="hero"] [data-document-role="heroVisual"] .site-builder-image {
                transform: scale(1);
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroCopy"] {
                align-content: end;
                grid-column: 1 / span 8 !important;
                grid-row: 1 / -1 !important;
                min-height: 100svh;
                padding: clamp(9rem, 18vh, 13rem) clamp(1rem, 3vw, 3rem) clamp(4rem, 9vh, 7rem);
                position: relative;
                z-index: 2;
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroCopy"] :where(.site-builder-text-h1, .site-builder-text-paragraph, .site-builder-text),
            [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroProof"] {
                color: var(--atelier-ivory) !important;
                text-shadow: 0 4px 30px rgba(14, 4, 12, .32);
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroHeading"] {
                font-size: clamp(4rem, 9.3vw, 9.2rem) !important;
                max-width: 9ch !important;
            }

            [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroFloatingNote"] {
                bottom: clamp(1rem, 4vw, 2.5rem);
                left: auto;
                right: clamp(1rem, 4vw, 3.5rem);
                z-index: 3;
            }

            [data-web-template="atelier-muse"] :where(
                [data-document-role="editorialVisual"],
                [data-document-role="transformationPortrait"],
                [data-document-role="transformationDetail"],
                [data-document-role="lookbookImage"]
            ) {
                overflow: hidden;
            }

            [data-web-template="atelier-muse"] :where(
                [data-document-role="editorialVisual"],
                [data-document-role="transformationPortrait"],
                [data-document-role="transformationDetail"],
                [data-document-role="lookbookImage"]
            ) .site-builder-image {
                filter: saturate(.82) contrast(1.03);
                transition: filter 650ms ease, transform 900ms cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="atelier-muse"] [data-web-region="lookbookGallery"] .site-builder-grid-item:nth-child(2) {
                position: relative;
                z-index: 2;
            }

            [data-web-template="atelier-muse"] [data-web-region="lookbookGallery"] .site-builder-card {
                box-shadow: 0 24px 72px rgba(0, 0, 0, .24);
            }

            [data-web-template="atelier-muse"] :where([data-web-region="bookingCta"], [data-web-region$="Booking"]) {
                overflow: hidden;
            }

            [data-web-template="atelier-muse"] :where([data-web-region="bookingCta"], [data-web-region$="Booking"])::after {
                border: 1px solid rgba(255, 250, 240, .16);
                border-radius: 50%;
                content: "";
                height: min(48vw, 620px);
                pointer-events: none;
                position: absolute;
                right: -16vw;
                top: -70%;
                width: min(48vw, 620px);
            }

            [data-web-template="atelier-muse"]:not([data-web-template-editor="true"])[data-atelier-runtime-ready] [data-atelier-reveal] {
                opacity: 0;
                transform: translate3d(0, 22px, 0);
                transition: opacity 560ms ease, transform 680ms cubic-bezier(.2,.72,.2,1);
            }

            [data-web-template="atelier-muse"]:not([data-web-template-editor="true"])[data-atelier-runtime-ready] [data-atelier-reveal][data-atelier-revealed] {
                opacity: 1;
                transform: translate3d(0, 0, 0);
            }

            @media (hover: hover) and (pointer: fine) {
                [data-web-template="atelier-muse"] :where(
                    [data-document-role="editorialVisual"],
                    [data-document-role="transformationPortrait"],
                    [data-document-role="transformationDetail"],
                    [data-document-role="lookbookImage"]
                ):hover .site-builder-image {
                    filter: saturate(1) contrast(1.04);
                    transform: scale(1.025);
                }

                [data-web-template="atelier-muse"] .site-builder-card:hover {
                    box-shadow: 0 20px 60px rgba(75, 31, 69, .12);
                    transform: translateY(-4px);
                }
            }

            [data-web-template="atelier-muse"] .atelier-mobile-menu-toggle {
                display: none;
            }

            @container (max-width: 900px) {
                [data-web-template="atelier-muse"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="atelier-muse"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="signatureStrip"] > .site-builder-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="signatureStrip"] > .site-builder-grid > .site-builder-grid-item {
                    border-right: 0;
                    grid-column: auto !important;
                    grid-row: auto !important;
                }

                [data-web-template="atelier-muse"] [data-document-role="heroVisual"] {
                    min-height: 100svh;
                }

                [data-web-template="atelier-muse"] [data-document-role="heroFloatingNote"] {
                    left: 1rem;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"] {
                    min-height: 70px;
                    padding-inline: 1rem;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] {
                    background: #270d23 !important;
                    color: var(--atelier-ivory);
                }

                [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="brand"] .site-builder-text,
                [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="brand"] .site-builder-text > span,
                [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text,
                [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text > span {
                    color: var(--atelier-ivory) !important;
                }

                [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled][data-atelier-mobile-menu-open] [data-document-role="brand"] .site-builder-text,
                [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled][data-atelier-mobile-menu-open] [data-document-role="brand"] .site-builder-text > span {
                    color: var(--atelier-ivory) !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] [data-site-navigation-root="true"] {
                    --site-navigation-accent: var(--atelier-coral-on-dark) !important;
                    --site-navigation-background: var(--site-primary) !important;
                    --site-navigation-border: rgba(255, 250, 240, .2) !important;
                    --site-navigation-muted: rgba(255, 250, 240, .76) !important;
                    --site-navigation-surface: rgba(255, 250, 240, .08) !important;
                    --site-navigation-text: var(--atelier-ivory) !important;
                    color: var(--atelier-ivory) !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="atelier-muse"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"] .atelier-mobile-menu-toggle {
                    align-items: center;
                    background: transparent;
                    border: 1px solid currentColor;
                    color: var(--atelier-ivory);
                    cursor: pointer;
                    display: grid;
                    gap: 6px;
                    height: 42px;
                    justify-self: end;
                    padding: 9px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 42px;
                    z-index: 3;
                }

                [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"][data-atelier-header-scrolled] .atelier-mobile-menu-toggle,
                [data-web-template="atelier-muse"]:not(:has([data-web-region="hero"])) [data-web-region="header"] .atelier-mobile-menu-toggle {
                    color: var(--site-text);
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] .atelier-mobile-menu-toggle {
                    color: var(--atelier-ivory);
                }

                [data-web-template="atelier-muse"] .atelier-mobile-menu-toggle > span {
                    background: currentColor;
                    display: block;
                    height: 1px;
                    transition: transform 180ms ease;
                    width: 21px;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] {
                    bottom: 0;
                    left: 0;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 10vh, 6rem);
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid rgba(255, 250, 240, .18);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2.2rem, 10vw, 4rem) !important;
                    font-weight: 520 !important;
                    letter-spacing: -.04em !important;
                    text-transform: none;
                    width: 100% !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroCopy"] {
                    grid-column: 1 !important;
                    padding: 8.5rem 1rem 4rem;
                }

                [data-web-template="atelier-muse"] [data-web-region="hero"] [data-document-role="heroHeading"] {
                    font-size: clamp(3.35rem, 16vw, 5.7rem) !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="hero"]::after {
                    background: linear-gradient(0deg, rgba(18, 7, 16, .9) 5%, rgba(31, 12, 28, .47) 67%, rgba(15, 5, 13, .38));
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1rem;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] .atelier-mobile-menu-toggle > span:first-child {
                    transform: translateY(3.5px) rotate(45deg);
                }

                [data-web-template="atelier-muse"] [data-web-region="header"][data-atelier-mobile-menu-open] .atelier-mobile-menu-toggle > span:last-child {
                    transform: translateY(-3.5px) rotate(-45deg);
                }
            }

            @container (max-width: 640px) {
                [data-web-template="atelier-muse"] .site-builder-section {
                    padding-inline: 1rem;
                }

                [data-web-template="atelier-muse"] [data-web-region="teamHero"] .site-builder-text-h1 {
                    font-size: clamp(54px, 9vw, 58px) !important;
                }

                [data-web-template="atelier-muse"] [data-web-region="signatureStrip"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="atelier-muse"] [data-document-role="heroVisual"] {
                    min-height: 100svh;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="atelier-muse"] *,
                [data-web-template="atelier-muse"] *::before,
                [data-web-template="atelier-muse"] *::after {
                    animation-duration: .01ms !important;
                    animation-iteration-count: 1 !important;
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }

                [data-web-template="atelier-muse"] [data-atelier-reveal] {
                    opacity: 1 !important;
                    transform: none !important;
                }
            }
        `}</style>
    );
}

function AtelierFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main
            className="px-5 py-20 sm:px-6"
            data-builder-document-region="page"
        >
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-8 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-medium [&_h1]:leading-[.92] [&_h1]:tracking-[-.05em] [&_h1]:text-[var(--site-text)] [&_h2]:mb-4 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-medium [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const atelierMuseTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "atelier-muse",
        name: "Kosmetický a kadeřnický salon",
        type: "website",
        category: "beauty",
        description: "Výrazná editoriální šablona pro kadeřnické a kosmetické salony a osobní studia.",
        previewImage: "/templates/atelier-muse/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["atelier-muse"],
        pageTypes: {
            home: {
                label: "Atelier homepage",
                slots: ["richContent"],
            },
            content: {
                label: "Atelier content page",
                slots: ["richContent"],
            },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline", "pill"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            atelierMuseTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                data-web-template="atelier-muse"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <AtelierStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <AtelierMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <AtelierFallbackPage
                            props={props}
                            design={design}
                            LinkComponent={LinkComponent}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
